import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getActionQueue,
  removeFromQueue,
  clearActionQueue,
  enqueueAction,
} from '../services/offlineStorageService';

/**
 * useOffline
 * Tracks online/offline status and exposes the queued-action API.
 * Optionally flushes the queue when connectivity is restored by calling
 * the provided `flushFn(action)` for each queued item.
 *
 * @param {Function} [flushFn] - async (action) => void  called per queued item on reconnect
 */
export function useOffline(flushFn) {
  const [isOnline, setIsOnline] = useState(() => navigator?.onLine ?? true);
  const [queue, setQueue] = useState(() => getActionQueue());
  const flushRef = useRef(flushFn);
  flushRef.current = flushFn;

  // Sync queue state from localStorage
  const refreshQueue = useCallback(() => {
    setQueue(getActionQueue());
  }, []);

  // Add an action to the queue
  const queueAction = useCallback((action) => {
    const item = enqueueAction(action);
    refreshQueue();
    return item;
  }, [refreshQueue]);

  // Remove a single action
  const dequeueAction = useCallback((id) => {
    removeFromQueue(id);
    refreshQueue();
  }, [refreshQueue]);

  // Flush all queued actions (called automatically on reconnect if flushFn provided)
  const flushQueue = useCallback(async () => {
    const items = getActionQueue();
    if (!items?.length) return;

    for (const action of items) {
      try {
        if (flushRef?.current) {
          await flushRef?.current(action);
        }
        removeFromQueue(action?.id);
      } catch (err) {
        console.warn('[useOffline] Failed to flush action:', action?.id, err?.message);
      }
    }
    refreshQueue();
  }, [refreshQueue]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-flush when coming back online
      if (flushRef?.current) {
        flushQueue();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [flushQueue]);

  return {
    isOnline,
    isOffline: !isOnline,
    queue,
    queueAction,
    dequeueAction,
    flushQueue,
    clearQueue: () => { clearActionQueue(); refreshQueue(); },
    queueCount: queue?.length ?? 0,
  };
}
